/* Compact sticky header (sections/header.liquid, "Collapse to one row on scroll").
   The header stays sticky at full height until the page has scrolled past it, then collapses to one row
   (styled in assets/sf-sections.css under .sf-header--compact). The height it loses is kept as a bottom
   margin on the header section, so the page below never jumps. Scrolling back to the top restores it.
   The switch is animated: the logo, menu, account and basket glide from their old place to the new one,
   the white bar grows or shrinks with them, and the search fades while it changes between field and icon. */
(() => {
  const section = document.querySelector('.section-header');
  if (!section || !section.querySelector('[data-sf-compact]')) return;

  const GAP = 8;
  const DURATION = 300;
  const EASING = 'cubic-bezier(0.2, 0, 0, 1)';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wrapper = section.querySelector('.header-wrapper');
  let full = 0;
  let compact = false;
  let ticking = false;
  let running = [];

  const apply = (on) => {
    compact = on;
    section.classList.toggle('sf-header--compact', on);
    section.style.marginBottom = on ? `${Math.max(0, full - section.offsetHeight)}px` : '';
  };

  const stopAnimations = () => {
    running.forEach((animation) => animation.cancel());
    running = [];
  };

  const movingParts = () =>
    [
      section.querySelector('.header__heading'),
      section.querySelector('.header__inline-menu'),
      section.querySelector('.header__icons .header__icon--account'),
      section.querySelector('.header__icons .header__icon--cart'),
    ].filter(Boolean);

  const fadingParts = () =>
    [section.querySelector('.header__icons .header__search'), ...section.querySelectorAll('.sf-header-contact')].filter(
      Boolean
    );

  // Switch layouts, then play each part from where it was to where it now sits
  const switchTo = (on) => {
    if (!wrapper || reduceMotion.matches) {
      stopAnimations();
      apply(on);
      return;
    }

    const parts = movingParts();
    const before = parts.map((part) => part.getBoundingClientRect());
    const heightBefore = wrapper.getBoundingClientRect().height;
    const marginBefore = parseFloat(getComputedStyle(section).marginBottom) || 0;

    stopAnimations();
    apply(on);

    const heightAfter = wrapper.getBoundingClientRect().height;
    if (Math.abs(heightAfter - heightBefore) < 1) return;
    const marginAfter = parseFloat(section.style.marginBottom) || 0;
    const timing = { duration: DURATION, easing: EASING };

    // Height and margin move together so their sum, and so the page below, stays still
    running.push(wrapper.animate([{ height: `${heightBefore}px` }, { height: `${heightAfter}px` }], timing));
    running.push(
      section.animate([{ marginBottom: `${marginBefore}px` }, { marginBottom: `${marginAfter}px` }], timing)
    );

    parts.forEach((part, i) => {
      const after = part.getBoundingClientRect();
      const dx = before[i].left - after.left;
      const dy = before[i].top - after.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      running.push(part.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], timing));
    });

    fadingParts().forEach((part) => {
      if (!part.getClientRects().length) return;
      running.push(part.animate([{ opacity: 0 }, { opacity: 0, offset: 0.3 }, { opacity: 1 }], timing));
    });

    running.forEach((animation) => {
      animation.onfinish = () => {
        running = running.filter((item) => item !== animation);
      };
    });
  };

  const measure = () => {
    stopAnimations();
    const was = compact;
    if (was) apply(false);
    full = section.offsetHeight;
    if (was) apply(true);
    update();
  };

  const update = () => {
    ticking = false;
    // Leave the header alone while a menu, the search or the account dialog is open
    if (section.querySelector('details[open]')) return;
    const y = window.scrollY;
    if (!compact && y > full + GAP) switchTo(true);
    else if (compact && y < full - GAP) switchTo(false);
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measure, 150);
  });
  window.addEventListener('load', measure);
  measure();
})();
